import type { GetApiV1ThreeLetterCodePartnerAdminsTopBanners200 } from '@fukuri-admins/apis/client/gen/model';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { generateReOrderRequest } from '../logics';
import { TopBannerListPresentation } from '../presentations';
import { useGetPartnerAdminsTopBanners } from './useGetPartnerAdminsTopBanners';
import { usePutPartnerAdminsTopBanner } from './usePutPartnerAdminsTopBanner';

type PresentationProps = React.ComponentProps<typeof TopBannerListPresentation>;

type IUseTopBannerList = () => {
  state: {
    data: GetApiV1ThreeLetterCodePartnerAdminsTopBanners200;
  };
  actions: {
    handleSelectedDropdownMenu: PresentationProps['onSelectedDropdownMenu'];
    handleReOrder: PresentationProps['onReOrder'];
  };
};

/**
 * トップバナー一覧のコンポーネントフック
 *
 * @returns state.data - APIから取得したトップバナー一覧データ
 * @returns actions.handleClickCreate - 新規作成ボタンクリック時のハンドラ
 * @returns actions.handleReOrder - 並び替えハンドラ
 */
export const useTopBannerList: IUseTopBannerList = () => {
  const threeLetterCode = getThreeLetterCode();
  const { showToast } = useToast();

  const { data, mutate } = useGetPartnerAdminsTopBanners({ threeLetterCode });

  const { trigger: triggerUpdate } = usePutPartnerAdminsTopBanner({
    threeLetterCode,
    options: {
      onSuccess: () => {
        showToast({
          message: 'バナーを更新しました。',
          status: 'success',
        });
        mutate();
      },
      onError: error => {
        const errors = error.response?.data?.errors;
        showToast({
          message: errors?.[0] ?? 'バナーの更新に失敗しました。',
          status: 'danger',
        });
      },
    },
  });

  /**
   * ドロップダウンメニューのアクション完了後のハンドラ
   * 編集・ステータス切替・削除などのアクション完了後に一覧を再取得する
   */
  const handleSelectedDropdownMenu = () => {
    mutate();
  };

  /**
   * 並び替えハンドラ
   * 並び替えをAPIを呼び出して更新する
   *
   * @param items - 並び替え対象のトップバナー一覧
   */
  const handleReOrder = async (items: PresentationProps['topBanners']) => {
    await Promise.all(
      items.map(async ({ id, title, transitionUrl, isActive }, index) => {
        const body = generateReOrderRequest({
          topBanner: {
            title,
            transitionUrl,
            isActive,
          },
          newSortOrder: index + 1,
        });
        await triggerUpdate({ topBannerId: id, body });
      })
    );
  };
  return {
    state: {
      data,
    },
    actions: {
      handleSelectedDropdownMenu,
      handleReOrder,
    },
  };
};
